import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBrandPlanConfig } from '../reduxToolkit/appSlice';

/**
 * BrandPlanProvider
 *
 * Manages automatic refresh of brand plan configuration:
 * 1. On mount: Checks if cache is valid, fetches if expired or missing
 * 2. Hourly interval: Checks cache expiry and refreshes if needed (24h TTL)
 * 3. On brand change: Automatically triggers via HeaderDropDown
 *
 * This provider should wrap the entire app (after auth providers)
 */
export const BrandPlanProvider = ({ children }) => {
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  const hasCheckedRef = useRef(false);

  // Get current brand from Redux
  const currentBrand = useSelector((state) => state.app.currentBrand);
  const { cacheExpiry, brand } = useSelector(
    (state) => state.app.brandPlanConfig,
  );

  // On mount: Always fetch fresh data on page load/refresh
  useEffect(() => {
    if (!currentBrand?.brand_id || hasCheckedRef.current) return;

    // Always fetch fresh data on page refresh
    // This ensures users always see the latest plan configuration
    dispatch(
      fetchBrandPlanConfig({
        brandId: currentBrand.brand_id,
        forceRefresh: true, // Force fresh fetch on page load
      }),
    );

    hasCheckedRef.current = true;
  }, [currentBrand?.brand_id, dispatch]);

  // Reset hasCheckedRef when brand changes to allow re-checking
  useEffect(() => {
    hasCheckedRef.current = false;
  }, [currentBrand?.brand_id]);

  // Hourly interval to check cache expiry (24-hour TTL)
  useEffect(() => {
    if (!currentBrand?.brand_id) return;

    // Check every hour if cache needs refresh
    intervalRef.current = setInterval(
      () => {
        const now = Date.now();

        if (cacheExpiry && now >= cacheExpiry) {
          dispatch(
            fetchBrandPlanConfig({
              brandId: currentBrand.brand_id,
              forceRefresh: true,
            }),
          );
        }
      },
      60 * 60 * 1000,
    ); // Check every 1 hour

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentBrand?.brand_id, cacheExpiry, dispatch]);

  return <>{children}</>;
};

export default BrandPlanProvider;
